import { useState, useEffect } from "react";
import { Phone, MapPin, Menu, X } from "lucide-react";
import { CLINIC } from "@/lib/clinic";

const NAV = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Reviews", href: "#reviews" },
  { label: "Visit", href: "#visit" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#FAFAF9]/75 border-b border-stone-200/60 shadow-sm"
          : "backdrop-blur-md bg-[#FAFAF9]/40 border-b border-transparent"
      }`}
    >
      <div className="container mx-auto px-6 sm:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Brand */}
          <a
            href="#top"
            data-testid="brand-link"
            className="flex items-center gap-2.5 group"
          >
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#3A564A] text-[#FAFAF9] font-display font-bold text-lg">
              D
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-display font-bold text-lg sm:text-xl text-stone-900 tracking-tight">
                Dent Square
              </span>
              <span className="font-devanagari text-[11px] text-stone-500 mt-0.5">
                {CLINIC.nameHindi} · Lucknow
              </span>
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-stone-700 rounded-full hover:bg-stone-900/5 hover:text-stone-900 transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-2">
            <a
              href={`tel:${CLINIC.phoneTel}`}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3A564A] hover:bg-[#2C4239] text-[#FAFAF9] text-sm font-semibold transition-all hover:scale-[1.02]"
            >
              <Phone className="w-4 h-4" />
              Call Clinic
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-stone-200 bg-white"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {open && (
          <div
            data-testid="mobile-nav"
            className="lg:hidden pb-5 pt-1 flex flex-col gap-1 border-t border-stone-200/60"
          >
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="px-3 py-3 text-base font-medium text-stone-800 rounded-lg hover:bg-stone-900/5"
              >
                {n.label}
              </a>
            ))}
            <a
              href={CLINIC.mapsLink}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-2 text-sm text-stone-600 px-3"
            >
              <MapPin className="w-4 h-4" /> {CLINIC.address}
            </a>
          </div>
        )}
      </div>
    </header>
  );
}
